export const DAY_TIME: Record<string, string> = {
  "911": "Wknd",
  "Last Man Standing": "M-Su",
  "Modern Family": "M-F",
  "Modern Family WKND": "Wknd",
  "Tamron Hall": "M-F",
  "The Rookie": "Wknd",
  "TMZ MF": "M-F",
  "TMZ Weekend": "Wknd",
  "25 Words or Less": "M-F",
  "Divorce Court": "M-F",
  "Karamo": "M-F",
  "Maury Povich Show": "M-F",
  "Steve Wilkos": "M-F",
  // Internal name matches Salesforce Programs__c.Name so the SF-lookup
  // stamp on + Add Show works without special-casing. Estimate file uses
  // the shorter "Front Office Sports" — see ESTIMATE_NAME_MAP below.
  "Front Office Sports Tonight": "M-Su",
};

/**
 * Initial period map — keyed by `${quarter}-${year}` (NOT just the
 * Q-letter) so plans that span the same Q-letter across two calendar
 * years (Scatter: 4Q'26 + 4Q'27) can resolve unambiguously. The
 * MediaPlanBuilder rebuilds this from the active estimate version's
 * quarters on load. These defaults match the legacy 25/26 calendar
 * the app boots into before any estimate version is selected.
 */
export const PERIOD_MAP: Record<string, string> = {
  "Q1-2026": "1Q'26",
  "Q2-2026": "2Q'26",
  "Q3-2026": "3Q'26",
  "Q4-2025": "4Q'25",
};

/**
 * Special tab key for the Summary view (program × quarter aggregated table).
 * Displayed as the first tab in the Media Plan Builder, before the quarterly tabs.
 */
export const SUMMARY_TAB_KEY = "Summary";

/**
 * Quarter tabs displayed in the Media Plan Builder, one per quarter in the
 * rolling 4-quarter window. Order is the display order (left-to-right).
 *
 * `key` is the unique tab identifier (matches the period string).
 * `quarter` and `year` match the values returned by the source-deals API on
 * each row, used to filter `allDeals` to the active tab's slice.
 *
 * Update this list when the planning window advances. Should match the
 * backend `DEFAULT_QUARTERS` in nosey-mrss-feed/src/services/snowflake/source-deals/get.ts.
 */
export const QUARTER_TABS: Array<{
  key: string;
  quarter: string;
  year: number;
  label: string;
}> = [
  { key: "4Q'25", quarter: "Q4", year: 2025, label: "4Q'25" },
  { key: "1Q'26", quarter: "Q1", year: 2026, label: "1Q'26" },
  { key: "2Q'26", quarter: "Q2", year: 2026, label: "2Q'26" },
  { key: "3Q'26", quarter: "Q3", year: 2026, label: "3Q'26" },
];

export const NETWORK_MAP: Record<string, string> = {
  "911": "ABC",
  "Last Man Standing": "ABC",
  "Modern Family": "ABC",
  "Modern Family WKND": "ABC",
  "The Rookie": "ABC",
  "Tamron Hall": "ABC",
  "Divorce Court": "FOX",
  "25 Words or Less": "FOX",
  "TMZ MF": "FOX",
  "TMZ Weekend": "FOX",
  "Karamo": "NBC",
  "Maury Povich Show": "NBC",
  "Steve Wilkos": "NBC",
  // FS1 (Fox Sports 1) — grouped under FOX for the network-banded table
  // display. If MVMS spins up a dedicated Sports network in this UI later,
  // move this entry (and update PROGRAM_ORDER + NETWORK_ORDER).
  "Front Office Sports Tonight": "FOX",
};

export const PROGRAM_DISPLAY: Record<string, string> = {
  "911": "911",
  "Last Man Standing": "Last Man Standing",
  "Modern Family": "Modern Family",
  "Modern Family WKND": "Modern Family Wknd",
  "The Rookie": "The Rookie",
  "Tamron Hall": "Tamron Hall with Bounce",
  "Divorce Court": "Divorce Court",
  "25 Words or Less": "25 Words",
  "TMZ MF": "TMZ",
  "TMZ Weekend": "TMZ Weekend",
  "Karamo": "Karamo",
  "Maury Povich Show": "Maury (AT) Repeats",
  "Steve Wilkos": "Steve Wilkos Show (AT)",
  "Front Office Sports Tonight": "Front Office Sports Tonight",
};

export const ESTIMATE_NAME_MAP: Record<string, string> = {
  "Modern Family WKND": "Modern Family Wknd",
  "Tamron Hall": "Tamron Hall with Bounce",
  "25 Words or Less": "25 Words",
  "TMZ MF": "TMZ",
  // MPB/SF calls this "Front Office Sports Tonight" but the MVMS
  // Syndication estimate workbook lists it as just "Front Office
  // Sports". Map SF name → estimate name so getEstimate() resolves.
  "Front Office Sports Tonight": "Front Office Sports",
};

export const NETWORK_ORDER = ["ABC", "FOX", "NBC"];

// (NETWORK_DISPLAY removed — the "ABC 4Q25 Quarter Totals"-style label is now
// derived at render-time from the active estimate version's quarter labels.
// See `quarterTotalsLabel` in MediaPlanBuilder.tsx.)

export const PROGRAM_ORDER: Record<string, string[]> = {
  ABC: ["911", "Last Man Standing", "Modern Family", "Modern Family WKND", "The Rookie", "Tamron Hall"],
  FOX: ["Divorce Court", "25 Words or Less", "TMZ MF", "TMZ Weekend", "Front Office Sports Tonight"],
  NBC: ["Karamo", "Maury Povich Show", "Steve Wilkos"],
};

export const ALL_PROGRAMS = [
  "911", "Last Man Standing", "Modern Family", "Modern Family WKND",
  "Tamron Hall", "The Rookie", "TMZ MF", "TMZ Weekend",
  "25 Words or Less", "Divorce Court", "Karamo", "Maury Povich Show", "Steve Wilkos",
  "Front Office Sports Tonight",
];

export const PLAN_LENGTHS = ["15", "30", "45", "60", "90", "120"];

export const SYSTEM_DEMOS = [
  "A18-49","A18-54","A25-54","A35+","A35-64",
  "W18-49","W18-64","W25-54","W35-64",
];

export const ALL_ESTIMATE_DEMOS = [
  "A18+","A18-24","A18-34","A18-49","A18-54","A18-64","A21-34","A21-39","A21-49",
  "A25+","A25-34","A25-44","A25-49","A25-54","A25-64","A35+","A35-49","A35-54",
  "A35-64","A40+","A45-64","A50+","A50-64","A55+","A65+","F12-34","FTEEN","K02-11",
  "K06-11","M12-34","M18+","M18-24","M18-34","M18-49","M18-64","M21-34","M21-39",
  "M25-34","M25-49","M25-54","M25-64","M35+","M35-54","M35-64","M45+","M50+","M55+",
  "P45+","TTEEN","V02+","V12-24","V12-34","W18+","W18-24","W18-34","W18-49","W18-54",
  "W18-64","W21-34","W21-39","W25+","W25-34","W25-44","W25-49","W25-54","W25-64",
  "W35+","W35-49","W35-54","W35-64","W45+","W50+","W50-64","W55+",
];

export const UNIVERSE_DATA: Record<string, number> = {
  HH:128400,  "V02+":321880, "A18+":258850, "A18-24":30660,  "A18-34":72450,
  "A18-49":136090, "A18-54":156020, "A18-64":196620, "A21-34":58610, "A21-39":80510,
  "A21-49":122250, "A25+":228190,   "A25-34":41790,  "A25-44":85110,  "A25-49":105430,
  "A25-54":125360, "A25-64":165960, "A35+":186400,   "A35-49":63640,  "A35-54":83570,
  "A35-64":124170, "A40+":164500,   "P45+":143080,   "A45-64":80850,  "A50+":122760,
  "A50-64":60530,  "A55+":102830,   "A65+":62230,    "M18+":126320,   "M18-24":15530,
  "M18-34":36610,  "M18-49":68350,  "M18-64":98100,  "M21-34":29640,  "M21-39":40600,
  "M25-34":21080,  "M25-49":52820,  "M25-54":62700,  "M25-64":82570,  "M35+":89710,
  "M35-54":41620,  "M35-64":61490,  "M45+":68060,    "M50+":57970,    "M55+":48090,
  "W18+":132530,   "W18-24":15130,  "W18-34":35840,  "W18-49":67740,  "W18-54":77790,
  "W18-64":98520,  "W21-34":28970,  "W21-39":39910,  "W25+":117400,   "W25-34":20710,
  "W25-44":42380,  "W25-49":52610,  "W25-54":62660,  "W25-64":83390,  "W35+":96690,
  "W35-49":31900,  "W35-54":41950,  "W35-64":62680,  "W45+":75020,    "W50+":64790,
  "W50-64":30780,  "W55+":54740,    "K02-11":38180,  "K06-11":23150,  "TTEEN":24850,
  "FTEEN":12120,   "V12-24":55510,  "V12-34":97300,  "F12-34":47960,  "M12-34":49340,
};